import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Twitter, Youtube, Mail, Phone, MapPin } from 'lucide-react';
import PageModal from './PageModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Slug'ları modal olarak aç (yasal sayfalar)
const MODAL_SLUGS = new Set([
  'kullanim-kosullari',
  'privacy',
  'kvkk-aydinlatma-metni',
  'mesafeli-satis-sozlesmesi',
  'tuketici-haklari',
]);

// path -> slug dönüştür
const pathToSlug = (path) => (path || '').replace(/^\/+/, '').replace(/\/+$/, '');

const Footer = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalPage, setModalPage] = useState(null); // {slug, title} or null
  
  const handleLinkClick = (e, path, name) => {
    const slug = pathToSlug(path);
    if (MODAL_SLUGS.has(slug)) {
      e.preventDefault();
      setModalPage({ slug, title: name });
    }
  };

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch(`${API_URL}/api/settings`);
        const data = await response.json();
        setSettings(data);
      } catch (error) {
        console.error('Footer ayarları yüklenirken hata:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  if (loading || !settings) {
    return (
      <footer className="bg-gray-900 text-gray-300">
        <div className="container mx-auto px-4 py-12">
          <div className="text-center">Yükleniyor...</div>
        </div>
      </footer>
    );
  }

  const { footerSettings } = settings;
  const enabledQuickLinks = footerSettings.quickLinks?.filter(link => link.enabled) || [];
  const enabledCategories = footerSettings.categories?.filter(cat => cat.enabled) || [];
  const enabledCustomSections = footerSettings.customSections?.filter(section => section.enabled) || [];

  // Calculate grid columns based on sections
  const totalSections = 1 + // About section (always present)
    (enabledQuickLinks.length > 0 ? 1 : 0) +
    (enabledCategories.length > 0 ? 1 : 0) +
    enabledCustomSections.length +
    1; // Contact section (always present)

  const gridCols = totalSections <= 2 ? 'grid-cols-1 md:grid-cols-2' :
                   totalSections === 3 ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' :
                   'grid-cols-1 md:grid-cols-2 lg:grid-cols-4';

  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="container mx-auto px-4 py-12">
        <div className={`grid ${gridCols} gap-8`}>
          {/* About */}
          <div>
            <div className="bg-gradient-to-r from-red-600 to-red-700 text-white px-4 py-2 font-bold text-lg inline-block transform -skew-x-12 mb-4">
              <span className="inline-block transform skew-x-12">⚡ Cep Premium</span>
            </div>
            <p className="text-sm mt-4 leading-relaxed">
              {footerSettings.description || 'Cep Premium'}
            </p>
            
            {/* Social Media */}
            {footerSettings.socialMedia && Object.values(footerSettings.socialMedia).some(item => item?.enabled) && (
              <div className="flex gap-4 mt-6">
                {footerSettings.socialMedia.facebook?.enabled && footerSettings.socialMedia.facebook.url && (
                  <a 
                    href={footerSettings.socialMedia.facebook.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                  >
                    <Facebook size={18} />
                  </a>
                )}
                {footerSettings.socialMedia.instagram?.enabled && footerSettings.socialMedia.instagram.url && (
                  <a 
                    href={footerSettings.socialMedia.instagram.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                  >
                    <Instagram size={18} />
                  </a>
                )}
                {footerSettings.socialMedia.twitter?.enabled && footerSettings.socialMedia.twitter.url && (
                  <a 
                    href={footerSettings.socialMedia.twitter.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                  >
                    <Twitter size={18} />
                  </a>
                )}
                {footerSettings.socialMedia.youtube?.enabled && footerSettings.socialMedia.youtube.url && (
                  <a 
                    href={footerSettings.socialMedia.youtube.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                  >
                    <Youtube size={18} />
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Quick Links */}
          {enabledQuickLinks.length > 0 && (
            <div>
              <h3 className="text-white font-semibold text-lg mb-4">Hızlı Linkler</h3>
              <ul className="space-y-2">
                {enabledQuickLinks.map((link, index) => (
                  <li key={index}>
                    <Link to={link.path} onClick={(e) => handleLinkClick(e, link.path, link.name)} className="hover:text-red-600 transition-colors text-sm">
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Categories */}
          {enabledCategories.length > 0 && (
            <div>
              <h3 className="text-white font-semibold text-lg mb-4">Kategoriler</h3>
              <ul className="space-y-2">
                {enabledCategories.map((category, index) => (
                  <li key={index}>
                    <Link to={category.path} className="hover:text-red-600 transition-colors text-sm">
                      {category.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Custom Sections */}
          {enabledCustomSections.map((section, index) => {
            const enabledLinks = section.links?.filter(link => link.enabled) || [];
            return enabledLinks.length > 0 ? (
              <div key={index}>
                <h3 className="text-white font-semibold text-lg mb-4">{section.title}</h3>
                <ul className="space-y-2">
                  {enabledLinks.map((link, linkIndex) => (
                    <li key={linkIndex}>
                      <Link to={link.path} onClick={(e) => handleLinkClick(e, link.path, link.name)} className="hover:text-red-600 transition-colors text-sm">
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null;
          })}

          {/* Contact */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-4">İletişim</h3>
            <ul className="space-y-3">
              {footerSettings.contact?.address && (
                <li className="flex items-start gap-3">
                  <MapPin size={18} className="text-red-600 flex-shrink-0 mt-1" />
                  <span className="text-sm">{footerSettings.contact.address}</span>
                </li>
              )}
              {footerSettings.contact?.phone && (
                <li className="flex items-center gap-3">
                  <Phone size={18} className="text-red-600 flex-shrink-0" />
                  <a href={`tel:${footerSettings.contact.phone.replace(/\s/g, '')}`} className="text-sm hover:text-red-600 transition-colors">
                    {footerSettings.contact.phone}
                  </a>
                </li>
              )}
              {footerSettings.contact?.email && (
                <li className="flex items-center gap-3">
                  <Mail size={18} className="text-red-600 flex-shrink-0" />
                  <a href={`mailto:${footerSettings.contact.email}`} className="text-sm hover:text-red-600 transition-colors">
                    {footerSettings.contact.email}
                  </a>
                </li>
              )}
            </ul>
            
            <div className="mt-6">
              <h4 className="text-white font-medium mb-2 text-sm">Bültene Abone Ol</h4>
              <div className="flex gap-2">
                <input
                  type="email"
                  placeholder="E-posta adresiniz"
                  className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-red-600 text-sm"
                />
                <button className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition-colors text-sm font-medium">
                  Abone Ol
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-gray-800 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-gray-400">
            © 2026 Cep Premium. Tüm hakları saklıdır.
          </p>
          <div className="flex items-center gap-3">
            <img 
              src="https://cdn.jsdelivr.net/gh/aaronfagan/svg-credit-card-payment-icons@main/flat/visa.svg" 
              alt="Visa" 
              className="h-8 rounded"
              data-testid="payment-icon-visa"
            />
            <img 
              src="https://cdn.jsdelivr.net/gh/aaronfagan/svg-credit-card-payment-icons@main/flat/mastercard.svg" 
              alt="Mastercard" 
              className="h-8 rounded"
              data-testid="payment-icon-mastercard"
            />
          </div>
        </div>
      </div>
      
      {/* Legal page modal */}
      {modalPage && (
        <PageModal
          slug={modalPage.slug}
          title={modalPage.title}
          onClose={() => setModalPage(null)}
        />
      )}
    </footer>
  );
};

export default Footer;
